/**
 * Export orchestrator (F3 / B5、Phase 5-6)。
 *
 * 対応フォーマット:
 *   - 'json' : OoKey native JSON (`format: 'ookey'`)
 *   - 'csv'  : ログイン主体のフラット CSV (相互運用用、card/identity は notes に集約)
 *   - 'zip'  : 上記 json + csv を 1 つの ZIP に同梱 (fflate、将来の添付ファイル同梱を見据える)
 *
 * いずれも **暗号化されていない平文**。UI 側で確認 modal + 警告 notice を表示すること。
 * 暗号化 export (PBKDF2 + AES-GCM のパスワード保護) は別 batch で追加予定。
 */

import { sessionStore } from '@features/auth/services/sessionStore'
import { loadVaultList } from '@features/vault/services/vaultFlow'
import { loadFolderList } from '@features/vault/services/folderFlow'
import type { OoKeyExport, OoKeyExportFolder, OoKeyExportItem } from '../types'
import { strToBytes, zipStore } from './zip'

export type ExportFormat = 'json' | 'csv' | 'zip'

export class ExportError extends Error {
  public constructor(public readonly messageKey: string) {
    super(messageKey)
    this.name = 'ExportError'
  }
}

interface ExportData {
  items: OoKeyExportItem[]
  folders: OoKeyExportFolder[]
}

/** Vault と folder を fetch + 復号する。 */
const loadExportData = async (): Promise<ExportData> => {
  const session = sessionStore.getState()
  if (session.kind !== 'unlocked') {
    throw new ExportError('tools.export.error.no_session')
  }

  let items: OoKeyExportItem[]
  let folders: OoKeyExportFolder[]

  try {
    const result = await loadVaultList(session.symmetricKey)

    items = result.items.map(it => ({ folderId: it.folderId, payload: it.payload }))
  } catch {
    throw new ExportError('tools.export.error.fetch_failed')
  }

  try {
    const folderList = await loadFolderList(session.symmetricKey)

    folders = folderList.map(f => ({ id: f.id, name: f.name }))
  } catch {
    folders = []
  }

  return { items, folders }
}

/** OoKey native JSON 文字列を返す。 */
export const buildExportJson = async (): Promise<string> => {
  const { items, folders } = await loadExportData()

  const exp: OoKeyExport = {
    format: 'ookey',
    version: 1,
    exportedAt: new Date().toISOString(),
    folders,
    items,
  }

  return JSON.stringify(exp, null, 2)
}

/** 1 セルを RFC 4180 でエスケープする (`,` `"` 改行を含むなら "..." で囲む)。 */
const csvCell = (value: string): string => {
  if (/[",\n\r]/u.test(value)) {
    return `"${value.replace(/"/gu, '""')}"`
  }

  return value
}

/**
 * 全 type を往復できるフラット CSV を返す。
 *
 * 列: name,type,folder,favorite + 各 type の固有フィールド (login / card /
 * identity / note)。固有列は該当しない type の行では空欄。`importFlow` の CSV
 * パーサは `type` 列を見てこれらの列から各 payload を復元する。
 *
 * 注意: CSV は固定列のため、複数 URL は先頭のみ・カスタムフィールドは非対応
 * (どちらも欠落する)。無損失で往復したい場合は JSON 経路を使うこと。
 */
const CSV_HEADER = [
  'name',
  'type',
  'folder',
  'favorite',
  'username',
  'password',
  'url',
  'totp',
  'cardNumber',
  'cardHolder',
  'cardExpiry',
  'cardCvv',
  'firstName',
  'lastName',
  'phone',
  'email',
  'body',
  'notes',
] as const

export const buildExportCsv = async (): Promise<string> => {
  const { items, folders } = await loadExportData()
  const folderName = new Map(folders.map(f => [f.id, f.name]))

  const lines = [CSV_HEADER.join(',')]

  for (const it of items) {
    const p = it.payload
    const login = p.type === 'login' ? p : null
    const card = p.type === 'card' ? p : null
    const identity = p.type === 'identity' ? p : null
    const note = p.type === 'note' ? p : null

    const row = [
      p.name,
      p.type,
      it.folderId !== null ? (folderName.get(it.folderId) ?? '') : '',
      p.favorite ? 'true' : '',
      login?.username ?? '',
      login?.password ?? '',
      // CSV は単一 URL 列のみ。複数 URL のうち先頭を出力する (無損失は JSON 経路)。
      login?.uris?.[0]?.uri ?? login?.url ?? '',
      login?.totpSecret ?? '',
      card?.cardNumber ?? '',
      card?.cardHolder ?? '',
      card?.cardExpiry ?? '',
      card?.cardCvv ?? '',
      identity?.firstName ?? '',
      identity?.lastName ?? '',
      identity?.phone ?? '',
      identity?.email ?? '',
      note?.body ?? '',
      p.notes ?? '',
    ]

    lines.push(row.map(csvCell).join(','))
  }

  return lines.join('\r\n')
}

/** json + csv を 1 つの ZIP にまとめた bytes を返す。 */
export const buildExportZip = async (): Promise<Uint8Array> => {
  const [json, csv] = await Promise.all([buildExportJson(), buildExportCsv()])

  return zipStore({
    'ookey-vault.json': strToBytes(json),
    'ookey-vault.csv': strToBytes(csv),
  })
}

/** Blob + a tag download をブラウザに発火する (string / binary 両対応、DI 可能)。 */
export const triggerDownload = (
  filename: string,
  content: string | Uint8Array,
  mimeType: string,
): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  const blob = new Blob([content as BlobPart], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')

  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // 少し遅延して revoke (a tag click 直後の navigation を妨げないため)
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

/** Convenience: build + download を 1 操作で。 */
export const runExport = async (format: ExportFormat = 'json'): Promise<void> => {
  const stamp = new Date().toISOString().replace(/[:.]/gu, '-')

  if (format === 'csv') {
    const csv = await buildExportCsv()

    triggerDownload(`ookey-export-${stamp}.csv`, csv, 'text/csv;charset=utf-8')

    return
  }

  if (format === 'zip') {
    const zip = await buildExportZip()

    triggerDownload(`ookey-export-${stamp}.zip`, zip, 'application/zip')

    return
  }

  const json = await buildExportJson()

  triggerDownload(`ookey-export-${stamp}.json`, json, 'application/json')
}
